// src/components/adminLandings/AdminLandings.jsx
//
// /admin/landings (admins): which landing page (qrproofs.com/?page=...) and
// which Google Ads keyword bring visits, demo requests and signups (backend
// routes/adminLandings.ts). Visits are counted anonymously; a demo or a
// signup is a commercial lead that kept its origin.
import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Box, Paper, Alert, CircularProgress, Chip, Link,
  Table, TableHead, TableRow, TableCell, TableBody, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import useRoleAuth from '../../utils/useRoleAuth';
import { landingStats } from '../../utils/adminLandingsApi';

export const PAGE_NAMES = {
  borne: 'Borne de notation',
  'avis-verifies': 'Avis vérifiés',
  conciergerie: 'Conciergerie',
  statistiques: 'Statistiques et quartier',
  evenements: 'Événements dans l’app',
};
// Under this many visits, a conversion rate says little.
const MIN_VISITS = 100;

const rate = (r) => (r.visits ? `${(((r.demos + r.signups) / r.visits) * 100).toFixed(1)} %` : '—');
const FewVisits = ({ visits }) =>
  visits < MIN_VISITS ? <Chip size="small" variant="outlined" label="trop peu de visites" sx={{ ml: 1 }} /> : null;

function Numbers({ r }) {
  return (
    <>
      <TableCell align="right">{r.visits}</TableCell>
      <TableCell align="right">{r.demos}</TableCell>
      <TableCell align="right">{r.signups}</TableCell>
      <TableCell align="right">{rate(r)}</TableCell>
    </>
  );
}

const NumberHeads = () => (
  <>
    <TableCell align="right">Visites</TableCell>
    <TableCell align="right">Démos</TableCell>
    <TableCell align="right">Inscriptions</TableCell>
    <TableCell align="right">Conversion</TableCell>
  </>
);

export default function AdminLandings() {
  const { ready, forbidden, loading, getToken } = useRoleAuth(['ROLE_ADMIN']);
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    // getToken() is synchronous (utils/useRoleAuth.js): the current token.
    landingStats(days, getToken())
      .then((d) => { if (!cancelled) { setData(d); setError(''); } })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [ready, days, getToken]);

  if (loading) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  if (forbidden) return <Container sx={{ py: 4 }}><Alert severity="error">Réservé aux administrateurs.</Alert></Container>;

  const keywords = (data?.keywords || []).filter((k) => k.page === selected);

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Pages d’entrée</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 780 }}>
        Chaque fonctionnalité a sa page : qrproofs.com/?page=borne, avis-verifies, conciergerie, statistiques, evenements.
        Les visites sont comptées sans cookie ni donnée personnelle ; une démo ou une inscription est comptée quand le prospect
        est arrivé par une de ces pages. Cliquez sur une page pour voir ses mots-clés Google Ads.
      </Typography>

      <ToggleButtonGroup size="small" exclusive value={days} onChange={(_e, v) => v && setDays(v)} sx={{ mb: 2 }}>
        <ToggleButton value={7}>7 jours</ToggleButton>
        <ToggleButton value={30}>30 jours</ToggleButton>
        <ToggleButton value={90}>90 jours</ToggleButton>
      </ToggleButtonGroup>

      {error && <Alert severity="error" sx={{ mb: 2 }}>Impossible de charger les chiffres ({error}).</Alert>}
      {!data && !error && <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>}

      {data && (
        <Paper variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead><TableRow><TableCell>Page</TableCell><NumberHeads /></TableRow></TableHead>
            <TableBody>
              {data.pages.map((p) => (
                <TableRow
                  key={p.page}
                  hover
                  selected={selected === p.page}
                  onClick={() => setSelected(selected === p.page ? null : p.page)}
                  sx={{ cursor: 'pointer' }}
                  data-testid={`page-${p.page}`}
                >
                  <TableCell>
                    <b>{PAGE_NAMES[p.page] || p.page}</b>
                    <FewVisits visits={p.visits} />
                    <Link
                      href={`/?page=${p.page}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      sx={{ display: 'block', fontSize: 12 }}
                    >
                      /?page={p.page}
                    </Link>
                  </TableCell>
                  <Numbers r={p} />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {data && selected && (
        <>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            {PAGE_NAMES[selected]} : mots-clés et campagnes
          </Typography>
          {keywords.length === 0 ? (
            <Typography color="text.secondary">Aucune visite sur cette période.</Typography>
          ) : (
            <Paper variant="outlined">
              <Table size="small">
                <TableHead><TableRow><TableCell>Mot-clé</TableCell><TableCell>Campagne</TableCell><NumberHeads /></TableRow></TableHead>
                <TableBody>
                  {keywords.map((k) => (
                    <TableRow key={`${k.utm_term}|${k.utm_campaign}`}>
                      <TableCell>{k.utm_term || <i>sans mot-clé (lien direct, mail…)</i>}<FewVisits visits={k.visits} /></TableCell>
                      <TableCell>{k.utm_campaign || '—'}</TableCell>
                      <Numbers r={k} />
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </>
      )}
    </Container>
  );
}
